import { PeriodKpis } from "@cryptors/api-client";
import { Paper, Stack, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface Props {
  data: PeriodKpis;
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

/**
 * Client requested to disable it for now.
 */
const DISPLAY_AVERAGE = false;

export const EngagementSection = ({ data }: Props) => {
  // Scans are absent only when QRFY is unavailable; filtered views use the
  // QR ids associated with their effective collections.
  const hasScans = data.timeSeries.some((p) => p.scans != null);
  // Keep the full ISO date as the x key (unique) — formatting to "18 juin"
  // drops the year, producing duplicate categories over multi-year ranges and
  // making recharts map hovers to the wrong point. Format only for display.
  const series = data.timeSeries.map((p) => ({
    date: p.date,
    captures: p.captures,
    ...(hasScans ? { scans: p.scans ?? 0 } : {}),
  }));

  const avg = data.averageCapturesPerCollection;
  const avgLine =
    avg !== null ? series.map((s) => ({ ...s, moyenne: avg })) : series;
  const displayAverage = DISPLAY_AVERAGE && avg !== null;

  return (
    <Stack gap={2}>
      <Typography variant="h6">Engagement des joueurs</Typography>
      <Paper sx={{ p: 2 }} elevation={1}>
        <Typography variant="caption" color="text.secondary">
          Captures d'œuvres sur la période
          {displayAverage && (
            <>
              {" "}
              · moyenne par collection : <strong>{avg}</strong>
            </>
          )}
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={avgLine}
            margin={{ top: 16, right: 16, left: 16, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
            <XAxis dataKey="date" tickFormatter={formatDate} minTickGap={40} />
            <YAxis />
            <Tooltip labelFormatter={(label) => formatDate(String(label))} />
            <Legend />
            <Line
              type="monotone"
              dataKey="captures"
              name="Captures totales"
              stroke="#1976d2"
              strokeWidth={2}
              dot={false}
            />
            {hasScans && (
              <Line
                type="monotone"
                dataKey="scans"
                name="Scans QR"
                stroke="#d14200"
                strokeWidth={2}
                dot={false}
              />
            )}
            {displayAverage && (
              <Line
                type="monotone"
                dataKey="moyenne"
                name="Moyenne par collection"
                stroke="#f57c00"
                strokeDasharray="4 4"
                strokeWidth={2}
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    </Stack>
  );
};
